<template>
  <ClientOnly>
    <template #fallback>
      <ReviewShowcasePlaceholder
        :average-rating="averageRating"
        :review-count="reviewCount"
        :reviews-length="reviews.length"
        :review-type="reviewType"
        :review-breakdown="reviewBreakdown"
      />
    </template>

    <div class="px-4 py-4 max-w-screen-xl m-auto">
      <div
        class="flex flex-col md:flex-row items-center justify-center gap-12 md:gap-24 mb-8"
      >
        <ReviewShowcaseTitle
          :average-rating="averageRating"
          :review-count="getRealReviewsCount"
          :review-type="reviewType"
        />
        <ReviewBreakdown
          v-if="
            reviewBreakdown &&
            getRealReviewsCount > 0 &&
            hasReviewBreakdownData(reviewBreakdown)
          "
          :breakdown="reviewBreakdown"
          @click:stars="onBreakdownStarsClick"
        />
      </div>
      <div class="overflow-hidden">
        <ErrorBoundary>
          <component
            :is="wrapperComponent"
            data-test-id="review-showcase-container"
            :items-length="amountOfReviewsToRender"
            :class="{
              'flex flex-col gap-4 w-full': isMobile,
            }"
            :hide-navigation="computedReviews.length <= 3"
            @scroll:right="carouselIdx = carouselIdx + 1"
            @scroll:left="carouselIdx--"
          >
            <ReviewCard
              v-for="i in amountOfReviewsToRender"
              :key="i"
              data-test-id="review-card-1"
              v-bind="getReviewCardProps[i - 1]"
              open-reply-in-sidebar
              @click:image="clickOnImage(getReviewCardProps[i - 1].id)"
              @expand:partner-reply="clickOnImage(getReviewCardProps[i - 1].id)"
              @expand:review-content="
                clickOnImage(getReviewCardProps[i - 1].id)
              "
            >
              <template #title>
                <div class="items-baseline gap-x-2 gap-y-0.5">
                  <div
                    class="text-sm md:text-base font-semibold text-black"
                    data-test-id="review-title"
                  >
                    {{ getReviewCardProps[i - 1].reviewerName }}
                  </div>
                  <div
                    class="text-xs text-stone-500"
                    data-test-id="review-created-at"
                  >
                    {{ getReviewCardProps[i - 1].reviewedAt }}
                  </div>
                </div>
              </template>
            </ReviewCard>
            <div
              v-for="i in placeholderCardsCount"
              :key="`placeholder-${i}`"
              aria-hidden="true"
              data-test-id="review-card-placeholder"
              class="hidden min-[1000px]:block w-full lg:min-w-[290px] bg-stone-100/50 border border-stone-200 rounded-xl"
            />
          </component>
        </ErrorBoundary>
      </div>

      <div v-if="computedReviews.length > 6" class="justify-center flex mt-4">
        <Button
          variant="outlined"
          class="px-4 py-3 md:py-2 font-semibold"
          @click="isSidebarOpen = true"
        >
          {{ t('review.seeAll') }}
        </Button>
      </div>

      <ReviewSidebar
        :is-open="isSidebarOpen"
        :reviews="computedReviews"
        :average-rating="averageRating"
        :review-count="reviewCount"
        :selected-review-id="selectedReviewId"
        :review-type="reviewType"
        :event-permalink="eventPermalink"
        :review-breakdown="reviewBreakdown"
        :initial-rating-filter="initialRatingFilter"
        @close="closeSidebar"
        @update:selected-review-id="selectedReviewId = $event"
      />
    </div>
  </ClientOnly>
</template>
<script lang="ts" setup>
import dayjs from 'dayjs'
import type { Review, ReviewBreakdown } from '~/composables/types/event'
import { hasReviewBreakdownData } from '~/utils/reviewSidebarHelpers'

const { t, locale } = useI18n()
const { isMobile } = useDevice()

const emit = defineEmits<{
  (e: 'clear-open-review'): void
}>()

const {
  reviews = [],
  reviewCount = 0,
  averageRating = '',
  displayItemTitle = false,
  reviewType = 'event',
  eventPermalink,
  reviewBreakdown,
  openWithReviewId,
} = defineProps<{
  reviews: Review[]
  averageRating: string
  reviewCount: number
  supplierName: string
  supplierCreatedAt: string
  displayItemTitle: boolean
  reviewType?: 'event' | 'partner' | 'third-party'
  eventPermalink?: string
  reviewBreakdown?: Record<string, number>
  reviewBreakdown?: ReviewBreakdown | null
  openWithReviewId?: string
}>()

const isSidebarOpen = ref(false)
const selectedReviewId = ref<string | undefined>(undefined)
const initialRatingFilter = ref<number | null>(null)
const carouselIdx = ref(0)

const onBreakdownStarsClick = (stars: number) => {
  initialRatingFilter.value = stars
  isSidebarOpen.value = true
}

const clickOnImage = (id: string) => {
  selectedReviewId.value = id
  isSidebarOpen.value = true
}

const closeSidebar = () => {
  isSidebarOpen.value = false
  selectedReviewId.value = undefined
  initialRatingFilter.value = null
  emit('clear-open-review')
}

watch(
  () => openWithReviewId,
  (id) => {
    if (id) {
      selectedReviewId.value = id
      isSidebarOpen.value = true
    }
  },
  { immediate: true }
)

const getRealReviewsCount = computed(() => {
  const reviewsLength = reviews.length

  return reviewCount >= reviewsLength ? reviewCount : reviewsLength
})

const computedReviews = computed(
  () =>
    [...reviews]?.sort((a, b) => {
      return dayjs(a.created_at).isBefore(dayjs(b.created_at)) ? 1 : -1
    }) || []
)

const computedReviewsList = computed(() => {
  const length = computedReviews.value.length

  if (isMobile) return [...computedReviews.value].splice(0, 3)

  const adjustedIdx = (idx: number) => (length + (idx % length)) % length

  return [
    computedReviews.value[adjustedIdx(0 + carouselIdx.value)],
    computedReviews.value[adjustedIdx(1 + carouselIdx.value)],
    computedReviews.value[adjustedIdx(2 + carouselIdx.value)],
  ]
})

const amountOfReviewsToRender = computed(() =>
  Math.min(computedReviews.value.length, computedReviewsList.value.length)
)

// Fill the 3-card desktop row with blank cards when there are fewer reviews
const placeholderCardsCount = computed(() =>
  isMobile || amountOfReviewsToRender.value === 0
    ? 0
    : 3 - amountOfReviewsToRender.value
)

const getReviewCardProps = computed(() =>
  computedReviewsList.value.map((review, i) => ({
    id: review.id,
    averageRating: review.rating,
    reviewerName: review.customer,
    isTeamevent: false,
    verified: review.verified,
    reviewedAt: dateToFormat(review.created_at, 'MMMM D, YYYY', locale.value),
    reviewContent: review.review,
    images: review.media?.data,
    supplierReply: review?.supplier_reply,
    class: {
      'min-[1000px]:w-full  min-[1000px]:block': true,
      hidden: i > 0 && !isMobile,
      'md:min-h-[295px]': review.media?.data.length > 0,
      'md:min-h-[210px]': !review.media || review.media?.data.length === 0,
    },
    ...(review.item && displayItemTitle ? { itemTitle: review.item } : {}),
  }))
)

const wrapperComponent = computed(() =>
  isMobile ? 'div' : resolveComponent('HorizontalCarouselWrapper')
)
</script>

<style scoped>
svg {
  display: inline;
}
</style>
